import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { environment } from '@mzic/mzic-environments';
import { Observable, Subject, from, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import {
  ErrorCode,
  RefreshTokenRequest,
  RefreshTokenResponse,
  VerifyPasscodeError,
  VerifyPasscodeRequest,
  VerifyPasscodeResponse,
} from '../interfaces/collection.interface';
import {
  CollectionSessionCoordinator,
  CollectionSessionRefreshResponse,
} from '../session/collection-session-coordinator';
import {
  CollectionSessionStorage,
  StoredCollectionSessionV2,
  purgeLegacyCollectionSessionCredentials,
} from '../session/collection-session-storage';
import {
  CollectionLockManager,
  CollectionWebLock,
} from '../session/collection-web-lock';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly ngZone = inject(NgZone);
  private readonly sessionCoordinator = new CollectionSessionCoordinator(
    new CollectionSessionStorage(localStorage),
    new CollectionWebLock(browserLockManager(), (notification) =>
      this.ngZone.run(notification),
    ),
    {
      refresh: (snapshot) => this.refreshCapturedSession(snapshot),
    },
  );
  private fingerprintPromise: Promise<string> | null = null;
  private ipPromise: Promise<string | null> | null = null;

  /**
   * Subject that emits collectionId when token expires or refresh fails.
   * Components should subscribe to route user to passcode step.
   */
  private readonly tokenExpiredSubject = new Subject<string>();
  readonly tokenExpired$ = this.tokenExpiredSubject.asObservable();

  constructor() {
    purgeLegacyCollectionSessionCredentials(localStorage);
  }

  /**
   * Verify passcode and obtain JWT token
   */
  verifyPasscode(
    collectionId: string,
    passcode: string,
    apiUrl: string,
  ): Observable<string> {
    return this.sessionCoordinator.captureRevision(collectionId).pipe(
      switchMap((capturedRevision) =>
        from(this.buildDevicePayload()).pipe(
          map((device) => ({ capturedRevision, device })),
        ),
      ),
      switchMap(({ capturedRevision, device }) => {
        const request: VerifyPasscodeRequest = {
          passcode,
          device,
        };

        return this.http
          .post<VerifyPasscodeResponse>(
            `${apiUrl}/v1/collections/auth/${collectionId}/verify-passcode`,
            request,
          )
          .pipe(
            switchMap((response) =>
              this.sessionCoordinator.publishSession(
                collectionId,
                capturedRevision,
                {
                  access_token: response.access_token,
                  session_id: response.session_id,
                  expires_at_epoch_ms: Date.now() + response.expires_in * 1_000,
                },
              ),
            ),
            map((session) => session.access_token),
            catchError((error: unknown) =>
              error instanceof HttpErrorResponse
                ? this.handlePasscodeError(error)
                : throwError(() => error),
            ),
          );
      }),
    );
  }

  hasUsableSession(collectionId: string): Observable<boolean> {
    return this.sessionCoordinator.hasUsableSession(collectionId);
  }

  runWithValidSession<T>(
    collectionId: string,
    work: (snapshot: StoredCollectionSessionV2) => Observable<T>,
  ): Observable<T> {
    return this.sessionCoordinator.runWithValidSession(collectionId, work);
  }

  refreshAfterUnauthorized<T>(
    collectionId: string,
    failedRevision: number,
    work: (snapshot: StoredCollectionSessionV2) => Observable<T>,
  ): Observable<T> {
    return this.sessionCoordinator.refreshAfterUnauthorized(
      collectionId,
      failedRevision,
      work,
    );
  }

  invalidateCapturedSession(
    collectionId: string,
    capturedRevision: number,
  ): Observable<boolean> {
    return this.sessionCoordinator
      .invalidateCapturedSession(collectionId, capturedRevision)
      .pipe(
        tap((cleared) => {
          if (cleared) this.tokenExpiredSubject.next(collectionId);
        }),
      );
  }

  private refreshCapturedSession(
    snapshot: StoredCollectionSessionV2,
  ): Observable<CollectionSessionRefreshResponse> {
    return from(this.buildDevicePayload()).pipe(
      switchMap((device) => {
        const request: RefreshTokenRequest = {
          device: {
            fingerprint: device.fingerprint,
            model: device.model || 'Unknown',
            manufacturer: device.manufacturer || 'Unknown',
            os_version: device.os_version || 'Unknown',
            ip_address: device.ip_address,
          },
        };
        return this.http.post<RefreshTokenResponse>(
          `${environment.apiUrlLegacy}/api/authorization/chordium/v1/auth/refresh`,
          request,
          {
            headers: new HttpHeaders({
              Authorization: `Bearer ${snapshot.access_token}`,
              'X-Session-ID': snapshot.session_id,
            }),
          },
        );
      }),
    );
  }

  /**
   * Get or create cached device fingerprint
   */
  private async getDeviceFingerprint(): Promise<string> {
    if (!this.fingerprintPromise) {
      this.fingerprintPromise = this.generateFingerprint();
    }
    return this.fingerprintPromise;
  }

  /**
   * Generate device fingerprint using FingerprintJS
   */
  private async generateFingerprint(): Promise<string> {
    try {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      return result.visitorId;
    } catch (error) {
      console.error('Failed to generate fingerprint:', error);
      // Fallback to a basic fingerprint
      return this.generateFallbackFingerprint();
    }
  }

  /**
   * Fallback fingerprint generation if FingerprintJS fails
   */
  private generateFallbackFingerprint(): string {
    const nav = navigator as any;
    const screen = window.screen;
    const components = [
      nav.userAgent || '',
      nav.language || '',
      screen.colorDepth || '',
      screen.width || '',
      screen.height || '',
      new Date().getTimezoneOffset() || '',
    ];
    return btoa(components.join('|'));
  }

  /**
   * Build device payload with fingerprint, ip_address, and rich metadata
   */
  private async buildDevicePayload(): Promise<VerifyPasscodeRequest['device']> {
    const [fingerprint, ipAddress] = await Promise.all([
      this.getDeviceFingerprint(),
      this.getPublicIp(),
    ]);

    const metadata = this.getDeviceMetadata();

    return {
      fingerprint,
      model: this.getDeviceModel(),
      manufacturer: this.getDeviceManufacturer(),
      os_version: this.getOSVersion(),
      ip_address: ipAddress || undefined,
      metadata,
    };
  }

  /**
   * Resolve and cache public IP address using ipify; tolerate failures
   */
  private async getPublicIp(): Promise<string | null> {
    if (this.ipPromise) return this.ipPromise;

    this.ipPromise = fetch('https://api.ipify.org?format=json', {
      cache: 'no-store',
    })
      .then(async (response) => {
        if (!response.ok) return null;
        const data = (await response.json()) as { ip?: string };
        return data?.ip || null;
      })
      .catch(() => null);

    return this.ipPromise;
  }

  /**
   * Gather non-sensitive device metadata to aid device recognition
   */
  private getDeviceMetadata(): Record<string, any> {
    const nav = navigator as any;
    const conn = nav.connection || nav.mozConnection || nav.webkitConnection;

    return {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      locale: nav.language,
      languages: nav.languages,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      device_memory: nav.deviceMemory,
      hardware_concurrency: nav.hardwareConcurrency,
      effective_connection_type: conn?.effectiveType,
      user_agent_data: nav.userAgentData
        ? {
            brands: nav.userAgentData.brands,
            mobile: nav.userAgentData.mobile,
            platform: nav.userAgentData.platform,
          }
        : undefined,
    };
  }

  /**
   * Get device model (browser name)
   */
  private getDeviceModel(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Unknown';
  }

  /**
   * Get device manufacturer (OS platform)
   */
  private getDeviceManufacturer(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Windows')) return 'Microsoft';
    if (ua.includes('Mac')) return 'Apple';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Google';
    if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad'))
      return 'Apple';
    return 'Unknown';
  }

  /**
   * Get OS version
   */
  private getOSVersion(): string {
    const ua = navigator.userAgent;
    const match =
      ua.match(/Windows NT ([\d.]+)/) ||
      ua.match(/Mac OS X ([\d_]+)/) ||
      ua.match(/Android ([\d.]+)/) ||
      ua.match(/iOS ([\d_]+)/);
    return match ? match[1].replace(/_/g, '.') : 'Unknown';
  }

  /**
   * Handle passcode verification errors
   */
  private handlePasscodeError(error: HttpErrorResponse): Observable<never> {
    // Handle API error responses (typically 400, 401, 403, etc.)
    if (error.error) {
      // API returns nested structure: {"error":{"code":"...","message":"..."}}
      const nestedError = error.error?.error as
        | { code?: string; message?: string }
        | undefined;

      // Also support flat structure: {"error":"...","message":"..."}
      const serverError = error.error as Partial<VerifyPasscodeError> & {
        detail?: string;
      };

      const errorCode =
        nestedError?.code ||
        (typeof serverError.error === 'string'
          ? (serverError.error as ErrorCode)
          : null) ||
        'INVALID_PASSCODE';

      const errorMessage =
        nestedError?.message ||
        serverError.detail ||
        serverError.message ||
        'Invalid passcode. Please try again.';

      const passcodeError: VerifyPasscodeError = {
        error: errorCode as ErrorCode,
        message: errorMessage,
        detail: serverError.detail,
        attempts_remaining: serverError.attempts_remaining,
        backoff_seconds: serverError.backoff_seconds,
      };

      return throwError(() => passcodeError);
    }
    return throwError(() => ({
      error: 'UNKNOWN_ERROR',
      message: 'An unexpected error occurred. Please try again.',
    }));
  }
}

function browserLockManager(): CollectionLockManager | null {
  const locks = globalThis.navigator?.locks;
  if (!locks) return null;

  return {
    request: (
      name: string,
      options: {
        mode: 'shared' | 'exclusive';
        signal: AbortSignal;
      },
      callback: () => Promise<void>,
    ) => locks.request(name, options, callback).then(() => undefined),
  };
}
